import { toPng } from 'html-to-image';
import html2canvas from 'html2canvas';

export const QR_SAVE_SUCCESS_GALLERY_AR = 'تم الحفظ في حافظة الصور لديك بنجاح';
export const QR_SAVE_SUCCESS_DOWNLOAD_AR = 'تم تنزيل رمز QR بنجاح';
export const QR_SAVE_ERROR_AR = 'تعذر حفظ رمز QR، حاول مرة أخرى';

export function isAppleTouchDevice() {
    if (typeof navigator === 'undefined') return false;
    return (
        /iPad|iPhone|iPod/.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
}

function dataUrlToFile(dataUrl, filename) {
    const [header, base64] = String(dataUrl).split(',');
    const mime = /data:(.*?);base64/.exec(header)?.[1] || 'image/png';
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new File([bytes], filename, { type: mime });
}

/** Prefer the rendered QR <canvas> so the download is scannable and CORS-safe. */
export function getDisplayedQrCanvasDataUrl(containerEl) {
    const canvas = containerEl?.querySelector?.('canvas');
    if (!canvas || typeof canvas.toDataURL !== 'function') {
        throw new Error('QR canvas not found');
    }
    return canvas.toDataURL('image/png');
}

async function captureNodeAsPng(node) {
    const isIOS = isAppleTouchDevice();
    if (isIOS) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        const canvas = await html2canvas(node, {
            backgroundColor: '#ffffff',
            useCORS: true,
            allowTaint: false,
            scale: 2,
            logging: false,
            removeContainer: true,
        });
        return canvas.toDataURL('image/png');
    }
    return toPng(node, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#ffffff',
    });
}

/**
 * Build a PNG data URL from the QR modal content.
 * Tries the card capture first, then falls back to the QR canvas.
 */
export async function buildQrImageDataUrl(containerEl) {
    if (!containerEl) {
        throw new Error('QR container missing');
    }

    try {
        const dataUrl = await captureNodeAsPng(containerEl);
        if (dataUrl && dataUrl.startsWith('data:image')) {
            return dataUrl;
        }
    } catch (_) {
        /* fall through to canvas export */
    }

    return getDisplayedQrCanvasDataUrl(containerEl);
}

function triggerAnchorDownload(dataUrl, filename) {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename || 'qr-code.png';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
}

/**
 * Save/share the QR image. Returns how the image was delivered.
 * - `shared`: native share sheet (may save to Photos on iOS)
 * - `downloaded`: browser file download
 */
export async function saveQrImageDataUrl(dataUrl, filename = 'qr-code.png') {
    if (!dataUrl) {
        throw new Error('Missing image data');
    }

    const file = dataUrlToFile(dataUrl, filename);
    const shareData = { files: [file], title: filename };

    if (
        isAppleTouchDevice()
        && typeof navigator.share === 'function'
        && (!navigator.canShare || navigator.canShare(shareData))
    ) {
        await navigator.share(shareData);
        return 'shared';
    }

    triggerAnchorDownload(dataUrl, filename);
    return 'downloaded';
}

export async function downloadQrFromContainer(containerEl, filename = 'qr-code.png') {
    const dataUrl = await buildQrImageDataUrl(containerEl);
    const mode = await saveQrImageDataUrl(dataUrl, filename);
    return { mode, dataUrl };
}

export function successMessageForSaveMode(mode, lng = 'ar') {
    if (lng === 'en') {
        return mode === 'shared'
            ? 'Image saved successfully'
            : 'QR code downloaded successfully';
    }
    // Required Arabic success copy after a successful save/download initiation.
    return QR_SAVE_SUCCESS_GALLERY_AR;
}
